import api from './axios';

// ---- Types ----
export type TypeContrat = 'CDI' | 'CDD' | 'SIVP' | 'KARAMA' | 'STAGE' | 'INTERIM';
export type SituationFamiliale = 'CELIBATAIRE' | 'MARIE' | 'DIVORCE' | 'VEUF';
export type TypeConge = 'ANNUEL' | 'MALADIE' | 'MATERNITE' | 'PATERNITE' | 'SANS_SOLDE' | 'EXCEPTIONNEL';
export type StatutConge = 'DEMANDE' | 'APPROUVE' | 'REFUSE' | 'ANNULE';

export interface Employe {
  id: number;
  matricule: string;
  nom: string;
  prenom: string;
  nomComplet: string;
  cin?: string;
  numeroCnss?: string;
  poste?: string;
  departement?: string;
  email?: string;
  telephone?: string;
  rib?: string;
  dateEmbauche?: string;
  dateSortie?: string;
  typeContrat: TypeContrat;
  situationFamiliale: SituationFamiliale;
  chefDeFamille: boolean;
  nbEnfants: number;
  salaireBaseBrut: number;
  congesAnnuels: number;
  actif: boolean;
}

export type EmployeRequest = Partial<Omit<Employe, 'id' | 'nomComplet'>>;

export interface Conge {
  id: number;
  employeId: number;
  employeNom: string;
  typeConge: TypeConge;
  dateDebut: string;
  dateFin: string;
  nbJours: number;
  statut: StatutConge;
  motif?: string;
}

export interface CongeRequest {
  employeId: number;
  typeConge: TypeConge;
  dateDebut: string;
  dateFin: string;
  nbJours?: number;
  motif?: string;
}

export interface SoldeConge {
  employeId: number;
  employeNom: string;
  annee: number;
  droitAnnuel: number;
  joursPris: number;
  joursEnAttente: number;
  soldeRestant: number;
}

export interface Bulletin {
  id: number;
  employeId: number;
  employeNom: string;
  matricule: string;
  mois: number;
  annee: number;
  salaireBase: number;
  primes: number;
  salaireBrut: number;
  cnssSalarial: number;
  fraisProfessionnels: number;
  baseImposable: number;
  irpp: number;
  css: number;
  cnssPatronal: number;
  netAPayer: number;
  coutEmployeur: number;
  ecritureId?: number;
  ecritureNumero?: string;
}

export interface ApercuPaie {
  salaireBrut: number;
  cnssSalarial: number;
  fraisProfessionnels: number;
  baseImposable: number;
  irpp: number;
  css: number;
  cnssPatronal: number;
  netAPayer: number;
}

const rhService = {
  // ---- Salariés ----
  listEmployes: (actifs = false) =>
    api.get<Employe[]>('/rh/employes', { params: { actifs } }).then((r) => r.data),
  getEmploye: (id: number) => api.get<Employe>(`/rh/employes/${id}`).then((r) => r.data),
  createEmploye: (data: EmployeRequest) => api.post<Employe>('/rh/employes', data).then((r) => r.data),
  updateEmploye: (id: number, data: EmployeRequest) =>
    api.put<Employe>(`/rh/employes/${id}`, data).then((r) => r.data),
  deleteEmploye: (id: number) => api.delete(`/rh/employes/${id}`).then((r) => r.data),

  // ---- Congés ----
  listConges: (employeId?: number) =>
    api.get<Conge[]>('/rh/conges', { params: employeId ? { employeId } : {} }).then((r) => r.data),
  createConge: (data: CongeRequest) => api.post<Conge>('/rh/conges', data).then((r) => r.data),
  changerStatutConge: (id: number, statut: StatutConge) =>
    api.put<Conge>(`/rh/conges/${id}/statut`, { statut }).then((r) => r.data),
  deleteConge: (id: number) => api.delete(`/rh/conges/${id}`).then((r) => r.data),
  soldeConge: (employeId: number, annee?: number) =>
    api.get<SoldeConge>(`/rh/conges/solde/${employeId}`, { params: annee ? { annee } : {} }).then((r) => r.data),

  // ---- Paie ----
  listBulletins: (annee?: number, mois?: number) =>
    api.get<Bulletin[]>('/rh/paie', { params: { ...(annee ? { annee } : {}), ...(mois ? { mois } : {}) } }).then((r) => r.data),
  getBulletin: (id: number) => api.get<Bulletin>(`/rh/paie/${id}`).then((r) => r.data),
  apercuPaie: (salaireBrut: number, chefDeFamille: boolean, nbEnfants: number) =>
    api.post<ApercuPaie>('/rh/paie/apercu', { salaireBrut, chefDeFamille, nbEnfants }).then((r) => r.data),
  genererBulletin: (employeId: number, mois: number, annee: number, primes?: number) =>
    api.post<Bulletin>('/rh/paie', { employeId, mois, annee, primes }).then((r) => r.data),
  deleteBulletin: (id: number) => api.delete(`/rh/paie/${id}`).then((r) => r.data),
  // TF-082 — bulletin PDF + déclaration CNSS trimestrielle
  bulletinPdf: (id: number) =>
    api.get(`/rh/paie/${id}/pdf`, { responseType: 'blob' }).then((r) => r.data as Blob),
  declarationCnss: (annee: number, trimestre: number) =>
    api.get<DeclarationCnss>('/rh/paie/declaration-cnss', { params: { annee, trimestre } }).then((r) => r.data),
};

export interface DeclarationCnssLigne {
  employeId: number;
  employeNom: string;
  matricule: string;
  numeroCnss: string;
  nbBulletins: number;
  brut: number;
  cnssSalarial: number;
  cnssPatronal: number;
  cnssTotal: number;
}

export interface DeclarationCnss {
  annee: number;
  trimestre: number;
  lignes: DeclarationCnssLigne[];
  totalBrut: number;
  totalCnssSalarial: number;
  totalCnssPatronal: number;
  totalCnss: number;
}

export default rhService;
